import { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import toast from 'react-hot-toast';

const EVENT_STYLE = {
  'trial.expired':            { bg: '#FEE2E2', color: '#DC2626' },
  'trial.warning':            { bg: '#FEF3C7', color: '#92400E' },
  'subscription.expired':     { bg: '#FEE2E2', color: '#DC2626' },
  'subscription.warning':     { bg: '#FEF3C7', color: '#92400E' },
  'cron.done':                { bg: '#EDE9FE', color: '#5B21B6' },
  'tenant.activated':         { bg: '#D1FAE5', color: '#059669' },
  'auth.register':            { bg: '#DBEAFE', color: '#1D4ED8' },
  'auth.login':               { bg: '#F3F4F6', color: '#374151' },
  'webhook.payment.approved': { bg: '#D1FAE5', color: '#059669' },
  'webhook.payment.rejected': { bg: '#FEE2E2', color: '#DC2626' },
  'webhook.received':         { bg: '#F3F4F6', color: '#6B7280' },
};

export default function AdminTools() {
  const [logs,    setLogs]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [cronUrl, setCronUrl] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminAPI.logs({ limit: 100 });
      setLogs(r.data.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    // Descobre o token via /health
    fetch('/api/health')
      .then(r => r.json())
      .then(r => {
        const token = r.data?.cron_token;
        if (token) setCronUrl(`https://www.autoflowbrasil.com.br/api/cron/check?token=${token}`);
      })
      .catch(() => {});
  }, []);

  const runCron = async () => {
    setRunning(true);
    try {
      const r = await adminAPI.cronRun();
      const d = r.data.data;
      toast.success(`Concluído: ${d.expired} bloqueado(s), ${d.warned} aviso(s), ${d.emails_sent} e-mail(s).`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erro ao executar.');
    } finally { setRunning(false); }
  };

  // Summarize by event type from recent logs
  const summary = logs.reduce((acc, l) => {
    const base = l.event.split('.')[0];
    acc[base] = (acc[base] || 0) + 1;
    return acc;
  }, {});

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0F172A' }}>Ferramentas</h1>
        <button onClick={runCron} disabled={running}
          style={{ padding: '10px 20px', background: running ? '#93C5FD' : '#2563EB', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: running ? 'not-allowed' : 'pointer' }}>
          {running ? 'Executando...' : '▶ Verificar assinaturas'}
        </button>
      </div>

      {/* Stats from logs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
        {Object.entries(summary).map(([key, count]) => (
          <div key={key} style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#0F172A' }}>{count}</div>
            <div style={{ fontSize: '.75rem', color: '#6B7280', marginTop: 2 }}>{key}</div>
          </div>
        ))}
      </div>

      {/* Cron URL */}
      {cronUrl && (
        <div style={{ background: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: 10, padding: '14px 18px', marginBottom: 20 }}>
          <div style={{ fontSize: '.78rem', fontWeight: 700, color: '#374151', marginBottom: 6 }}>URL para cron job (cPanel → Cron Jobs):</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <code style={{ flex: 1, fontSize: '.72rem', color: '#0F172A', wordBreak: 'break-all', background: '#fff', border: '1px solid #E5E7EB', borderRadius: 6, padding: '6px 10px' }}>
              {cronUrl}
            </code>
            <button onClick={() => { navigator.clipboard.writeText(cronUrl); toast.success('Copiado!'); }}
              style={{ padding: '6px 12px', background: '#F3F4F6', border: '1.5px solid #E5E7EB', borderRadius: 6, cursor: 'pointer', fontSize: '.78rem', fontWeight: 600, flexShrink: 0 }}>
              Copiar
            </button>
          </div>
          <div style={{ fontSize: '.73rem', color: '#9CA3AF', marginTop: 6 }}>
            Configure como cron diário: <code>0 6 * * *</code> — ou execute manualmente pelo botão acima.
          </div>
        </div>
      )}

      {/* System Logs */}
      <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 700, color: '#0F172A' }}>Log do Sistema</span>
          <button onClick={load}
            style={{ padding: '5px 12px', background: '#F3F4F6', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '.8rem', fontWeight: 600 }}>
            ↺ Atualizar
          </button>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>Carregando...</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.83rem' }}>
              <thead>
                <tr style={{ background: '#F8FAFC' }}>
                  {['Data/Hora', 'Evento', 'Empresa', 'Descrição'].map(h => (
                    <th key={h} style={{ padding: '9px 16px', textAlign: 'left', color: '#6B7280', fontWeight: 600, fontSize: '.75rem', borderBottom: '1px solid #E5E7EB', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map(log => {
                  const sty = EVENT_STYLE[log.event] || { bg: '#F3F4F6', color: '#374151' };
                  return (
                    <tr key={log.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                      <td style={{ padding: '8px 16px', color: '#9CA3AF', fontSize: '.75rem', whiteSpace: 'nowrap' }}>
                        {new Date(log.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td style={{ padding: '8px 16px' }}>
                        <span style={{ background: sty.bg, color: sty.color, borderRadius: 6, padding: '2px 8px', fontSize: '.7rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                          {log.event}
                        </span>
                      </td>
                      <td style={{ padding: '8px 16px', color: '#374151' }}>
                        {log.tenant_name || <span style={{ color: '#D1D5DB' }}>—</span>}
                      </td>
                      <td style={{ padding: '8px 16px', color: '#6B7280' }}>{log.description}</td>
                    </tr>
                  );
                })}
                {logs.length === 0 && (
                  <tr>
                    <td colSpan={4} style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>
                      Nenhum log registrado ainda.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
